import './index.css'

import * as React from 'react'

import { Box } from '@mui/material'
import { CLASSIFICATION_TYPES } from './ClassificationTypes'
import { RateLogicFactory } from './RateLogicFactory'
import area from '@turf/area'

const GALLONS_TO_LITERS = 3.78541178
const HECTARES_TO_ACRES = 2.4710538146716536
const POUNDS_PER_KILOGRAM = 2.204622621848776
const METERSQ_PER_HECTARE = 10000.0
const METERSQ_PER_ACRE = 4046.8564224
const FLUID_OUNCES_PER_GALLON = 128.0
const OUNCES_PER_POUND = 16.0
const GRAMS_PER_KILOGRAM = 1000.0
const MILLILITERS_PER_LITER = 1000.0

const KILOGRAMS_PER_POUND = 1.0 / POUNDS_PER_KILOGRAM // 0.453592370
const POUNDS_TO_KILOGRAMS = KILOGRAMS_PER_POUND
const GALLONS_PER_ACRE_TO_LITERS_PER_HECTARE = GALLONS_TO_LITERS * HECTARES_TO_ACRES // 9.35395623   // gallons are US gallons
const LITERS_PER_HECTARE_TO_GALLONS_PER_ACRE = 1.0 / GALLONS_PER_ACRE_TO_LITERS_PER_HECTARE // 0.106907
const POUNDS_PER_ACRE_TO_KILOGRAMS_PER_HECTARE = POUNDS_TO_KILOGRAMS * HECTARES_TO_ACRES // 1.12085
const KILOGRAMS_PER_HECTARE_TO_POUNDS_PER_ACRE = 1.0 / POUNDS_PER_ACRE_TO_KILOGRAMS_PER_HECTARE // 0.892179
const METERSSQ_TO_HECTARES = 1.0 / METERSQ_PER_HECTARE
const ACRES_PER_METERSQ = 1.0 / METERSQ_PER_ACRE // 0.0002471044
const METERSQ_TO_ACRES = ACRES_PER_METERSQ // 0.0002471044

// ignore complexity rule
// eslint-disable-next-line complexity
export function Ratelegend({
    data,
    rateProperty = 'actualRate',
    monoChrome,
    classificationType = CLASSIFICATION_TYPES.MEAN,
    calculatedUnitProps = {
        uomPreference: 'standard',
        liquid: false,
    },
    customUnitProps,
    ...props
}) {
    const title_color = {
        color: 'primary.main',
        fontWeight: 'bold',
    }

    const rateLogic = new RateLogicFactory().getRateLogic(classificationType)

    let colors = rateLogic.getColors(monoChrome)
    // reverse colors. This make the above color list identical to the one used in the map
    colors = colors.reduce((acc, val) => [val, ...acc], [])

    const rSteps = rateLogic.getSteps(data, rateProperty)

    let max = data.features.reduce((a, b) => Math.max(a, b.properties[rateProperty]), 0)
    // reverse, the map needs ascending order, the legend needs descending.
    let steps = rSteps.reduce((acc, val) => {
        return [val, ...acc]
    }, [])

    let totalArea = 0
    if (data.features[0]) {
        totalArea = data.features.reduce((a, b) => a + area(b), 0)
    }
    const percentages = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    let counts = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    // Gets percentages with piece wise
    for (let i = 0; i < data.features.length; i += 1) {
        for (let j = 0; j < colors.length; j += 1) {
            // Mimicking the logic used by the mapbox step painter
            // https://docs.mapbox.com/style-spec/reference/expressions/#step
            if (data.features[i].properties[rateProperty] >= steps[j] || j === colors.length - 1) {
                percentages[j] += area(data.features[i]) / totalArea
                // Convert to acres/hectares and count
                counts[j] += Number(area(data.features[i]))
                break
            }
        }
    }

    // Convert to acres/hectares and count
    counts = counts.map((cur) => {
        if (customUnitProps) {
            return cur * customUnitProps.m2ToDisplayArea
        }
        else if (calculatedUnitProps.uomPreference === 'metric') {
            return cur * METERSSQ_TO_HECTARES
        }
        // imperial

        return cur * METERSQ_TO_ACRES
    })


    let conversionFactor = 1
    let unit = ''
    let areaLabel = ''
    if (customUnitProps) {
        areaLabel = customUnitProps.areaLabel
        conversionFactor = customUnitProps.baseToDisplayRate
    }
    else if (calculatedUnitProps.uomPreference === 'metric' && calculatedUnitProps.liquid) {
        unit = 'L'
        areaLabel = 'ha'
        if (steps[0] <= 1) {
            unit = 'mL'
            // conversion from L/ha to mL/ha
            conversionFactor = MILLILITERS_PER_LITER
        }
    }
    else if (calculatedUnitProps.uomPreference === 'metric' && !calculatedUnitProps.liquid) {
        unit = 'kg'
        areaLabel = 'ha'
        if (steps[0] <= 1) {
            unit = 'g'
            conversionFactor = GRAMS_PER_KILOGRAM
        }
    }
    else if (calculatedUnitProps.uomPreference === 'standard' && calculatedUnitProps.liquid) {
        unit = 'gal'
        areaLabel = 'ac'
        // conversion from L/ha(as it was stored in the database) to gal/ac
        conversionFactor = LITERS_PER_HECTARE_TO_GALLONS_PER_ACRE

        if (steps[0] * conversionFactor <= 1) {
            unit = 'fl oz'
            // conversion from gal/ac to fl oz/ac
            conversionFactor *= FLUID_OUNCES_PER_GALLON
        }
    }
    else if (calculatedUnitProps.uomPreference === 'standard' && !calculatedUnitProps.liquid) {
        unit = 'lb'
        areaLabel = 'ac'
        // conversion from kg/ha(as it was stored in the database) to lb/ac
        conversionFactor = KILOGRAMS_PER_HECTARE_TO_POUNDS_PER_ACRE
        if (steps[0] * conversionFactor <= 1) {
            unit = 'oz'
            // conversion from lbs/ac to oz/ac
            conversionFactor *= OUNCES_PER_POUND
        }
    }

    steps = steps.map((cur) => cur * conversionFactor)
    max *= conversionFactor

    let multiples = false
    for (const step of steps) {
        const stepCeil = Math.ceil(step)
        if (steps.some((val) => step !== val && Math.ceil(val) === stepCeil)) {
            multiples = true
            break
        }
    }

    let stepLabels = []
    if (multiples) {
        stepLabels = steps.map((cur, i) => {
            if (i === steps.length - 2) {
                return '>0.00'
            }
            else if (i === 0) {
                return '<' + (max > cur ? max : cur).toFixed(2).toString()
            }

            return cur.toFixed(2).toString()

        })
    }
    else {
        stepLabels = steps.map((cur, i) => {
            if (i === steps.length - 2) {
                return '>0'
            }
            else if (i === 0) {
                return '<' + Math.ceil(max > cur ? max : cur).toString()
            }

            return Math.ceil(cur).toString()

        })
    }

    return (
        <div className="app-rprt-legend-container" {...props}>
            <Box sx={title_color} className="app-rprt-title">
                Legend ({customUnitProps ? customUnitProps.rateLabel : `${unit}/${areaLabel}`})
            </Box>
            <div className="app-rprt-legend-content">
                <div className="app-rprt-legend-column">
                    {colors.map((dat, ind) => (
                        <div key={ind} className="app-rprt-flex-center app-rprt-map-color-legend-row">
                            <div className="app-rprt-color-legend-value">{stepLabels[ind]}</div>
                        </div>
                    ))}
                </div>
                <div className="app-rprt-legend-column">
                    {colors.map((dat, index) => (
                        <div key={index} className="app-rprt-flex app-rprt-map-color-legend-row">
                            <div className="app-rprt-color-legend-color" style={{ background: `${dat}` }} />
                        </div>
                    ))}
                </div>
                <div className="app-rprt-legend-column">
                    {percentages.map((dat, index) => (
                        <div key={index} className="app-rprt-legend-bar-container">
                            <div className="app-rprt-legend-bar" style={{ width: `${100 * dat}%` }} />
                        </div>
                    ))}
                </div>
                <div className="app-rprt-map-color-legend-container ">
                    <div className="app-rprt-legend-column">
                        {percentages.map((dat, index) => (
                            <div key={index} className="app-rprt-flex-left app-rprt-map-color-legend-row">
                                <div className="app-rprt-color-legend-value">{`${(dat * 100)
                                    .toFixed(2)
                                    .toString()}%`}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className=" app-rprt-map-color-legend-container">
                    <div className="app-rprt-legend-column">
                        {counts.map((dat, index) => (
                            <div key={index} className="app-rprt-flex-left app-rprt-map-color-legend-row">
                                <div className="app-rprt-color-legend-value">{`${dat
                                    .toFixed(2)
                                    .toString()} ${areaLabel}`}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
